import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { setAgentRefreshing } from "../store/actions";
import { queryClient } from "./queryClient";
// import { queryClient } from "../App";

export type AgentRefreshDetail = {
  resources: string[];
};

// Resource → React Query top-level key mapping.
// invalidateQueries with a prefix key invalidates ALL queries whose key starts with it,
// including mounted and unmounted components. Stale-while-revalidate means next mount refetches.
const RESOURCE_QUERY_KEYS: Record<string, string[]> = {
  workstations: ["workstations"],
  usecases: ["usecases"],
  parts: ["parts"],
  training: ["trainings"],
  trainings: ["trainings"],
  training_status: ["trainings"],
  data: ["parts"], // data change → part list + image counts
  images: ["parts"],
};

// Single global entry point for all agent-triggered silent background refreshes.
// - Invalidates React Query caches immediately (works for all components, mounted or not).
// - Suppresses the global spinner so page data refreshes silently.
// - Calls the optional component-local handler for pages that manage their own fetch state
//   (e.g. ManageData which uses a custom fetchData(), not React Query).
export const useAgentRefresh = (handler?: (resources: string[]) => void) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const listener = (event: Event) => {
      const customEvent = event as CustomEvent<AgentRefreshDetail>;
      const resources = customEvent.detail?.resources;
      if (!Array.isArray(resources)) return;

      // Suppress global spinner — this is a silent background refresh
      dispatch(setAgentRefreshing(true));
      setTimeout(() => dispatch(setAgentRefreshing(false)), 3000);

      // Invalidate React Query caches for all affected resources.
      // This is global: affects all components regardless of which page the user is on.
      const invalidated = new Set<string>();
      resources.forEach((r) => {
        const key = RESOURCE_QUERY_KEYS[r];
        if (key && !invalidated.has(key[0])) {
          queryClient.invalidateQueries({ queryKey: key });
          invalidated.add(key[0]);
        }
      });

      // Also call the local handler for non-React Query pages
      if (handler) handler(resources);
    };

    window.addEventListener("livis_agent_refresh", listener as EventListener);
    return () =>
      window.removeEventListener(
        "livis_agent_refresh",
        listener as EventListener
      );
  }, [handler, dispatch]);
};
